import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import TextAutoResize from "./TextAutoResize";
import { WindowData } from "../../RegularClassroomUser/utils";
import { isPlatformChatSupported } from "@/js/utils/chatUtils";
import { ChakraFAIcon } from "@/components/ui/Icon";

interface Props {
	classActive: boolean;
	handleOnChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
	handleKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
	inputMessage: string;
	handleMessageSend: () => void;
	chatError: string | null;
	handleChatEnable: () => void;
	isChatTurnedOff?: boolean;
	/** Window data for the student's device. Null if there is no window data coming in (i.e. the student is not online) */
	studentDeviceWindowData: WindowData | null;
}

const ChatInput = ({
	classActive,
	isChatTurnedOff = false,
	handleOnChange,
	handleKeyDown,
	inputMessage,
	handleMessageSend,
	chatError,
	handleChatEnable,
	studentDeviceWindowData,
}: Props): JSX.Element => {
	const { t } = useTranslation();

	if (chatError) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{chatError}</p>
			</div>
		);
	}

	if (!classActive) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{t("classroomChat.classInactiveMessage")}</p>
				<a href="https://docs.classwize.net/help/intro-classtools" target="_blank" rel="noopener noreferrer">
					{t("classroomChat.learnMore")}
				</a>
			</div>
		);
	}

	if (isChatTurnedOff) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{t("classroomChat.chatTurnedOffMessage")}</p>
				<button className="ClassroomChatMessagePane_enableChat" onClick={handleChatEnable}>
					{t("classroomChat.turnOnChat")}
				</button>
			</div>
		);
	}

	if (!studentDeviceWindowData) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{t("classroomChat.studentOfflineMessage")}</p>
			</div>
		);
	}

	if (!isPlatformChatSupported(studentDeviceWindowData)) {
		return (
			<div className="ClassroomChatMessagePane_controlsPaneDisabled">
				<p>{t("classroomChat.deviceNotSupportedMessage")}</p>
			</div>
		);
	}

	return (
		<div className="ClassroomChatMessagePane_controlsPane">
			<div
				className={clsx("ClassroomChatMessagePane_textInputWrapper", {
					"ClassroomChatMessagePane_textInput--hasContent": inputMessage.length,
				})}
			>
				<TextAutoResize
					autoComplete="off"
					className="ClassroomChatMessagePane_textInput"
					onChange={handleOnChange}
					placeholder={t("classroomChat.messagePlaceholder")}
					value={inputMessage}
					disabled={isChatTurnedOff}
					onKeyDown={handleKeyDown}
				/>
			</div>
			<button
				className="ClassroomChatMessagePane_sendButton"
				disabled={isChatTurnedOff || !inputMessage.length || inputMessage.length >= 1000}
				onClick={handleMessageSend}
				type="button"
			>
				<ChakraFAIcon icon="fa-paper-plane" variant="solid" color="inherit" boxSize={18} mx={2} />
			</button>
		</div>
	);
};

export default ChatInput;
